import Highcharts from 'highcharts';
import React, { useCallback, useEffect, useState } from 'react';
import HighchartsReact from 'highcharts-react-official';
import ReactPortal from './ReactPortal';
import { CANVAS_PADDING_HORIZONTAL } from './ReportCanvas/canvasConstants';

const Y_DISTANCE_FROM_POINT = 15;

const getHighestPoint = (
  point: Highcharts.Point,
  points: Highcharts.Point[] | null,
): Highcharts.Point => {
  if (!points) {
    return point;
  }
  let highestPoint = points[0];

  // Find the highest point among the series
  points.forEach((p) => {
    if (
      p.plotY !== undefined &&
      highestPoint.plotY !== undefined &&
      p.plotY < highestPoint.plotY
    ) {
      highestPoint = p;
    }
  });

  return highestPoint;
};

/**
 * There is a race condition on first mounting the tooltip where
 * labelWidth is 0.
 *
 * This function detects that and then provides a rough estimate of what
 * the label width should be
 */
const getLabelWidth = ({
  points,
  baseLabelWidth,
}: {
  points: Highcharts.Point[] | null;
  baseLabelWidth: number;
}) => {
  if (baseLabelWidth !== 0) {
    return baseLabelWidth;
  }

  // Single point label
  if (!points) {
    return 160;
  }

  // Multiple point label
  return 270;
};

/**
 * There is a race condition on first mounting the tooltip where
 * labelHeight is 0.
 *
 * This function detects that and then provides a rough estimate of what
 * the label width should be
 */
const getLabelHeight = ({
  points,
  baseLabelHeight,
}: {
  points: Highcharts.Point[] | null;
  baseLabelHeight: number;
}) => {
  if (baseLabelHeight !== 0) {
    return baseLabelHeight;
  }

  // Single point label
  if (!points) {
    return 110;
  }

  // Multiple point label
  return 180;
};

// This bridge allows us to use the Context API within tooltips.
const HighchartsTooltipBridge = ({
  chart,
  positionMode,
  children,
}: {
  chart: Highcharts.Chart;
  positionMode: 'multi-shared-points' | 'auto';
  children(
    formatterContext: Highcharts.TooltipFormatterContextObject,
  ): JSX.Element;
}) => {
  const [formatterContext, setFormatterContext] =
    useState<Highcharts.TooltipFormatterContextObject | null>(null);
  const [tooltipDivId] = useState<string>(`chart-tooltip-${chart.index}`);

  const multipleSharedPointsPositioner = useCallback(
    ({
      baseLabelWidth,
      baseLabelHeight,
      point,
      chart,
    }: {
      baseLabelWidth: number;
      baseLabelHeight: number;
      point: Highcharts.Point;
      chart: Highcharts.Chart;
    }) => {
      const points = chart.hoverPoints;
      let highestPoint = getHighestPoint(point, points);
      const labelWidth = getLabelWidth({
        baseLabelWidth,
        points,
      });
      const labelHeight = getLabelHeight({
        baseLabelHeight,
        points,
      });

      /**
       * Start with the x,y such that the top left of the tooltip is at
       * the highest point in the chart
       */
      const baseTooltipX = (highestPoint.plotX || 0) + chart.plotLeft;
      const baseTooltipY = (highestPoint.plotY || 0) + chart.plotTop;

      /**
       * Adjust the Y such that the tooltip is positioned above the
       * highest point, ensuring all points are visible, with a small
       * margin between the point and the body of the tooltip
       */
      const tooltipY = baseTooltipY - labelHeight - Y_DISTANCE_FROM_POINT;

      /**
       * Adjust the X such that the tooltip is centered with the point,
       * such that a straight line can be drawn from the highest point
       * through the center of the tooltip
       *
       * Additionally, adjust the X such that the tooltip does not flow off the screen
       * to the left or right. This rule takes priority over centering
       * the tooltip
       */
      const tooltipX = (() => {
        const centeredX = baseTooltipX - labelWidth / 2;

        const leftMostPointOfCenteredTooltip = centeredX - labelWidth;
        const rightMostPointOfCenteredTooltip = centeredX + labelWidth;
        // We multiply the padding by 2 to reflect it being present on both sides
        const paddingOffset = CANVAS_PADDING_HORIZONTAL * 2;

        if (
          rightMostPointOfCenteredTooltip >
          chart.chartWidth - paddingOffset
        ) {
          /**
           * When the centered position will cause the tooltip to flow
           * off the chart to the right, align it such that the
           * hovered point is below the bottom right corner of the tooltip
           */
          return baseTooltipX - labelWidth;
        }

        if (leftMostPointOfCenteredTooltip + labelWidth < 0) {
          /**
           * When the centered position will cause the tooltip to flow
           * off the chart to the left, align it such that the
           * hovered point is below the bottom left corner of the tooltip
           */
          return baseTooltipX;
        }

        return centeredX;
      })();

      return { x: tooltipX, y: tooltipY };
    },
    [],
  );

  useEffect(() => {
    if (chart) {
      const formatter: Highcharts.TooltipFormatterCallbackFunction = function (
        props,
      ) {
        const key = this.points ? (this.points.length > 1 ? 'a' : 'b') : 'b';
        setFormatterContext(this);
        return `<div id="${tooltipDivId}" key=${key}></div>`;
      };

      chart.update({
        tooltip: {
          formatter,
          useHTML: true,
          positioner:
            positionMode === 'auto'
              ? undefined
              : function (baseLabelWidth, baseLabelHeight, point) {
                  if (positionMode === 'multi-shared-points') {
                    return multipleSharedPointsPositioner({
                      baseLabelHeight,
                      baseLabelWidth,
                      point,
                      chart: this.chart,
                    });
                  }

                  const e = new Error('');
                  e.name = `ChartV2: Unknown position mode found: ${positionMode}`;
                  throw e;
                },
        },
      });
    }
  }, [chart, multipleSharedPointsPositioner, positionMode, tooltipDivId]);

  if (!formatterContext) {
    return null;
  }

  return (
    <ReactPortal elementId={tooltipDivId}>
      {children(formatterContext)}
    </ReactPortal>
  );
};

interface ChartProps<T> {
  options: Highcharts.Options;
  TooltipComponent: ({
    point,
    points,
  }: {
    point: T | undefined; // When tooltip.shared: false
    points: T[] | undefined; // When tooltip.shared: true
  }) => JSX.Element | null;
  AdditionalComponent?: React.ReactNode;
  positionMode?: 'auto' | 'multi-shared-points';
  testId?: string;
}

const ChartV2 = <T extends Highcharts.Point>({
  options,
  TooltipComponent,
  AdditionalComponent,
  positionMode,
  testId,
}: ChartProps<T>) => {
  const [chart, setChart] = useState<Highcharts.Chart | undefined>();

  return (
    <>
      <HighchartsReact
        options={options}
        callback={setChart}
        highcharts={Highcharts}
        containerProps={{
          'data-testid': testId,
          style: {
            height: '100%',
            width: '100%',
          },
        }}
      />
      {!!chart && !!AdditionalComponent && <>{AdditionalComponent}</>}
      {!!chart && (
        <>
          <div id={`tooltip-${chart.index}`} />
          <HighchartsTooltipBridge
            chart={chart}
            positionMode={positionMode || 'auto'}
          >
            {(tooltipContext) => (
              <TooltipComponent
                point={tooltipContext.point as T | undefined}
                points={
                  tooltipContext.points
                    ? (tooltipContext.points
                        .map((p) => p.point)
                        .filter((p) => p.x !== null && p.y !== null) as T[])
                    : undefined
                }
              />
            )}
          </HighchartsTooltipBridge>
        </>
      )}
    </>
  );
};

export default ChartV2;
